<template>
  <div class="row-height">
    <div class="no-padding col-xl-12 col-lg-12 col-md-12">
      <BCard class="row-height">
        <BRow style="padding: 0 15px">
          <div class="col-xl-6 col-lg-6 col-sm-12">
            <h2 class="font-weight-bolder" style="color: #1B4332 ;">
              {{ $t("invoices") }}
            </h2>
          </div>
          <div class="col-xl-6 col-lg-6 col-sm-12 d-flex justify-content-end">
            <BButton variant="primary" @click="openAddInvoice">
              {{ $t("add_new_invoice(s)") }}
            </BButton>
          </div>
        </BRow>

        <BCol class="mb-5 col-md-12 px-1">
          <Table
            :showFilter="true"
            hover
            :fields="fields"
            :isSeller="true"
            :payload="tablePayload"
            :updateData="tableUpdateTrigger"
            @row-clicked="goToDetails"
            :url="'/invoice/list'"
          >
            <template #toolbar-filters>
              <BFormGroup
                :label="$t('status')"
                label-for="invoice-status-filter"
                label-size="sm"
                class="mb-0 invoices-status-filter mr-2"
              >
                <BFormSelect
                  id="invoice-status-filter"
                  v-model="statusFilterKey"
                  size="sm"
                  :options="statusFilterOptions"
                  @change="onStatusFilterChange"
                />
              </BFormGroup>
            </template>

            <template #cell(id)="data">
              <BLink
                style="width:55px;"
                class="badge badge-light-primary"
                :href="'/invoice/' + data.item.id"
              >
                I-{{ data.item.id }}
              </BLink>
            </template>

            <template #cell(debtor)="data">
              <span>{{ data.item.debtor_name || '-' }}</span>
            </template>

            <template #cell(due_date)="data">
              <span
                v-if="data.item.due_date"
                :class="{'text-danger': data.item.is_overdue}"
                style="position:relative;"
              >
                {{ data.item.due_date }}
                <span
                  v-if="data.item.is_overdue"
                  style="position: absolute; top: -4px; right: -20px"
                >
                  <FeatherIcon icon="AlertTriangleIcon" size="16" />
                </span>
              </span>
              <span v-else>-</span>
            </template>

            <template #cell(amount)="data">
              <span v-if="data.item.amount != null">
                {{ amountFormat(data.item.amount, data.item.currency_name) }}
              </span>
              <span v-else>-</span>
            </template>

            <template #cell(invoice_number)="data">
              <span v-html="data.item.invoice_number || '-'" />
            </template>

            <template #cell(status)="data">
              <span :class="getStatus(data).class">
                {{ $t(getStatus(data).label) }}
              </span>
            </template>

            <template #cell(custom)="data">
              <BButton
                v-if="showSellButton(data.item)"
                class="mt-1 web"
                @click="goToDetails(data.item)"
                variant="primary"
                size="sm"
              >
                {{ $t(data.item.status === 16 ? 'confirm' : 'sell') }}
              </BButton>
              <BButton
                v-b-tooltip.top
                :title="$t('read_pdf')"
                class="mt-1 ml-1 web"
                @click="downloadPdf(data.item.id)"
                variant="outline-success"
                size="sm"
              >
                <FeatherIcon icon="BookOpenIcon"/>
              </BButton>
            </template>
          </Table>
        </BCol>
      </BCard>
    </div>
  </div>
</template>

<script>
import Table from "@/components/common/Table.vue";
import fileDownload from "js-file-download";
import { CurrencyAmountFormat } from "@core/utils/CurrencyAmountFormat";

const BASE_STATUS_DISPLAY = {
  0: { class: "text-success", label: "Open" },
  1: { class: "text-success", label: "active_marketplace_offer" },
  2: { class: "text-danger", label: "rejected" },
  3: { class: "text-secondary", label: "scoring_required" },
  4: { class: "text-secondary", label: "scored" },
  5: { class: "text-secondary", label: "click_sell_invoice" },
  8: { class: "text-secondary", label: "soon_goes_active" },
  9: { class: "text-secondary", label: "in_sale" },
  10: { class: "text-secondary", label: "sold" },
  11: { class: "text-secondary", label: "settled" },
  12: { class: "text-secondary", label: "get_offer_status" },
  13: { class: "text-danger", label: "invoice_default" },
  14: { class: "text-danger", label: "deactivated" },
  15: { class: "text-warning", label: "removed_from_marketplace" },
};

const BASE_STATUS_FILTER_DEFINITIONS = [
  { key: "0", status: 0, label: "Open" },
  { key: "1", status: 1, label: "active_marketplace_offer" },
  { key: "2", status: 2, label: "rejected" },
  { key: "3", status: 3, label: "scoring_required" },
  { key: "4", status: 4, label: "scored" },
  { key: "5", status: 5, label: "click_sell_invoice" },
  { key: "6-0", status: 6, is_reverse: 0, label: "debtor_confirmation_required" },
  { key: "6-1", status: 6, is_reverse: 1, label: "supplier_confirmation_required" },
  { key: "8", status: 8, label: "soon_goes_active" },
  { key: "9", status: 9, label: "in_sale" },
  { key: "10", status: 10, label: "sold" },
  { key: "11", status: 11, label: "settled" },
  { key: "13", status: 13, label: "invoice_default" },
  { key: "14", status: 14, label: "deactivated" },
  { key: "15", status: 15, label: "removed_from_marketplace" },
  { key: "16-0", status: 16, purchase_confirmed: 0, label: "required_seller_confirmation" },
  { key: "16-1", status: 16, purchase_confirmed: 1, label: "waiting_admin_approval" },
];

const FIELDS = [
  { text: "ID", label: "ID", align: "start", key: "id", sortable: true },
  { label: "debtor", align: "start", key: "debtor", sortable: false },
  { label: "original_invoice_number", align: "start", key: "invoice_number", sortable: false },
  { label: "due_date", align: "start", key: "due_date", sortable: false },
  { label: "original invoice amount", align: "start", key: "amount", sortable: false },
  { label: "status", key: "status", customSlot: true, sortable: false },
  { label: "", customSlot: true, key: "custom", class: "text-right" },
];

export default {
  components: { Table },
  data() {
    return {
      statusFilterKey: null,
      tableUpdateTrigger: 0,
      globalSettings: 0,
      fields: FIELDS,
    };
  },
  created() {
    this.loadGlobalSettings();
  },
  computed: {
    statusFilterDefinitions() {
      const defs = [...BASE_STATUS_FILTER_DEFINITIONS];

      if (this.globalSettings) {
        defs.push({ key: "7", status: 7, label: "rejected" });
        defs.push({ key: "12", status: 12, label: "get_offer_status" });
      } else {
        defs.push({ key: "7-12", statuses: [7, 12], label: "get_offer_status" });
      }

      return defs;
    },
    statusFilterOptions() {
      return [
        { value: null, text: this.$t("select_one") },
        ...this.statusFilterDefinitions.map((def) => ({
          value: def.key,
          text: this.$t(def.label),
        })),
      ];
    },
    tablePayload() {
      if (!this.statusFilterKey) {
        return null;
      }

      const def = this.statusFilterDefinitions.find(
        (d) => d.key === this.statusFilterKey
      );
      if (!def) {
        return null;
      }

      const payload = {};

      if (def.statuses) {
        payload.invoice_statuses = def.statuses;
      } else {
        payload.invoice_status = def.status;
      }

      if (def.is_reverse !== undefined) {
        payload.is_reverse = def.is_reverse;
      }

      if (def.purchase_confirmed !== undefined) {
        payload.purchase_confirmed = def.purchase_confirmed;
      }

      return payload;
    },
  },
  methods: {
    async loadGlobalSettings() {
      try {
        const res = await this.$http.post("/settings/get/get_offer_later");
        if (res?.data?.value) {
          this.globalSettings = parseInt(res.data.value, 10) || 0;
        }
      } catch (_) {
        // keep default filter/status behaviour
      }
    },
    onStatusFilterChange() {
      this.tableUpdateTrigger++;
    },
    resolveStatusDisplay(status, item = {}) {
      if (status === 6) {
        return item.is_reverse === 1
          ? { class: "text-secondary", label: "supplier_confirmation_required" }
          : { class: "text-secondary", label: "debtor_confirmation_required" };
      }

      if (status === 7) {
        return this.globalSettings
          ? { class: "text-danger", label: "rejected" }
          : { class: "text-secondary", label: "get_offer_status" };
      }

      if (status === 16) {
        return {
          class: "text-secondary",
          label: item.purchase_confirmed
            ? "waiting_admin_approval"
            : "required_seller_confirmation",
        };
      }

      return BASE_STATUS_DISPLAY[status] || null;
    },
    getStatus({ value, item }) {
      return this.resolveStatusDisplay(value, item) || { class: "", label: "" };
    },
    amountFormat(amount, currency) {
      return CurrencyAmountFormat(amount, currency);
    },
    showSellButton(item) {
      return item.status === 0
        || item.status === 5
        || (item.status === 16 && !item.purchase_confirmed);
    },
    goToDetails(item) {
      if (item?.id) {
        this.$router.push("/invoice/" + item.id).catch(() => {});
      }
    },
    downloadPdf(id) {
      this.$store.commit("app/ON_SIDEBAR_CLICK", false);
      this.$http
        .post("/invoice/download/" + id, {}, { responseType: "blob" })
        .then((response) => {
          let filename = "invoice.pdf";
          const disposition = response.headers["content-disposition"];
          if (disposition?.includes("filename=")) {
            filename = disposition
              .split("filename=")[1]
              .split(";")[0]
              .replace(/['"]/g, "");
          }
          fileDownload(response.data, filename);
        });
    },
    openAddInvoice() {
      if (this.$store.getters["app/checkRequiredSigningGate"]) return;
      this.$router.push("/invoices/add-new");
    },
  },
};
</script>

<style scoped>
.invoices-status-filter {
  flex: 0 1 300px;
  min-width: 180px;
  max-width: 350px;
}
</style>
